import { v4 as uuidv4 } from 'uuid';
import { MileageEntries, MileageEntry } from '../../models/mileage.model';
import { utcFromDate } from 'src/app/shared/utils/date-utils';

export class MileageArrayHelper {
  private static sortEntries(entries: MileageEntry[]): MileageEntry[] {
    return [...entries].sort((a, b) => {
      const dateComparison = a.date.getTime() - b.date.getTime();
      if (dateComparison !== 0) {
        return dateComparison;
      }

      const odoComparison = a.odo - b.odo;
      if (odoComparison !== 0) {
        return odoComparison;
      }

      return a.id!.localeCompare(b.id!);
    });
  }

  private static checkForDuplicates(
    entries: MileageEntry[],
    newEntry: MileageEntry,
    isUpdate: boolean
  ): void {
    // Check for duplicate date and odometer
    const duplicateEntry = entries.find(
      (e) =>
        e.id !== newEntry.id &&
        e.date.getTime() === newEntry.date.getTime() &&
        e.odo === newEntry.odo
    );

    if (duplicateEntry) {
      throw new Error(
        `A mileage entry with the same date and odometer reading already exists.`
      );
    }
  }

  private static validateOdometerSequence(sortedEntries: MileageEntry[]): void {
    for (let i = 1; i < sortedEntries.length; i++) {
      if (
        sortedEntries[i].date.getTime() > sortedEntries[i - 1].date.getTime() &&
        sortedEntries[i].odo < sortedEntries[i - 1].odo
      ) {
        throw new Error(
          `Error: Mileage logs follow a timeline. Each odometer reading must fall between the readings of the entries before and after it. Please make your edits again.`
        );
      }
    }
  }

  static upsertEntry(
    currentData: MileageEntries | null,
    entryFromForm: MileageEntry
  ): MileageEntries {
    // Initialize with empty array if no data exists
    const currentEntries = currentData?.entries || [];

    let entryToProcess: MileageEntry;
    let isUpdate = false;

    if (entryFromForm.id) {
      // Editing an existing entry
      const existingEntryIndex = currentEntries.findIndex(
        (e) => e.id === entryFromForm.id
      );

      if (existingEntryIndex === -1) {
        entryToProcess = {
          ...entryFromForm,
          id: uuidv4(),
          date: utcFromDate(entryFromForm.date),
        };
      } else {
        entryToProcess = {
          ...entryFromForm,
          date: utcFromDate(entryFromForm.date),
        };
        isUpdate = true;
      }
    } else {
      // Adding a new entry
      entryToProcess = {
        ...entryFromForm,
        id: uuidv4(),
        date: utcFromDate(entryFromForm.date),
      };
    }

    // Check for duplicates before adding/updating
    MileageArrayHelper.checkForDuplicates(
      currentEntries,
      entryToProcess,
      isUpdate
    );

    // If updating, replace the existing entry
    if (isUpdate) {
      const updatedEntries = currentEntries.map((entry) =>
        entry.id === entryToProcess.id ? entryToProcess : entry
      );
      return { entries: MileageArrayHelper.sortEntries(updatedEntries) };
    }

    // Otherwise, add the new entry
    const updatedEntries = [...currentEntries, entryToProcess];
    const sortedEntries = MileageArrayHelper.sortEntries(updatedEntries);
    MileageArrayHelper.validateOdometerSequence(sortedEntries);

    return { entries: sortedEntries };
  }

  static deleteEntry(
    currentData: MileageEntries | null,
    entryId: string
  ): MileageEntries {
    if (!currentData || !currentData.entries.length) {
      throw new Error('No mileage data to delete from');
    }

    const entryIndex = currentData.entries.findIndex((e) => e.id === entryId);
    if (entryIndex === -1) {
      throw new Error('Mileage entry not found');
    }

    const updatedEntries = currentData.entries.filter((e) => e.id !== entryId);
    return { entries: updatedEntries };
  }
}
